import { instantToZonedIso } from '@littlebird/action-cards';
import { addDays } from 'date-fns/addDays';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { format } from 'date-fns/format';
import { isSameYear } from 'date-fns/isSameYear';
import { nextMonday } from 'date-fns/nextMonday';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';

// Date values in a card are ISO strings in one of five forms: a calendar day
// ("2026-09-25"), a UTC instant ("…Z"), an instant with an offset ("…+05:30"), a
// wall clock with no zone ("2026-09-25T09:00:00"), or a Todoist due wall clock
// ("2026-09-25 09:00"). A picked value is written back in the form it came in, so the
// server sees the same shape it sent.

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
// An older client sends a Todoist due to Todoist as is, so a pick must not add the `T` or seconds
const DUE_WALL_CLOCK = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
const ZONE_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/i;

export const isDateOnly = (value: string): boolean => DATE_ONLY.test(value);

// `parseISO` reads a calendar day as local midnight; `new Date` would read it as UTC
// midnight, which is the day before anywhere west of UTC
export function parseDateValue(value: string | undefined): Date | null {
  if (!value) return null;
  const date = parseISO(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function atFixedOffset(instant: Date, offset: string): string {
  const [, sign, hours, minutes] =
    /^([+-])(\d{2}):?(\d{2})$/.exec(offset) ?? [];
  const offsetMinutes =
    (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes));
  const wall = new Date(instant.getTime() + offsetMinutes * 60_000)
    .toISOString()
    .slice(0, 19);
  return `${wall}${sign}${hours}:${minutes}`;
}

// A named timezone wins over the old offset, so a move across a DST change carries
// the offset the zone has at the new instant
export function writeDateValue(
  next: Date,
  like: string,
  timezone?: string
): string {
  if (like === '' || isDateOnly(like)) return format(next, 'yyyy-MM-dd');
  const zone = ZONE_SUFFIX.exec(like)?.[1];
  if (!zone) {
    return format(
      next,
      DUE_WALL_CLOCK.test(like) ? 'yyyy-MM-dd HH:mm' : "yyyy-MM-dd'T'HH:mm:ss"
    );
  }
  if (zone.toUpperCase() === 'Z') {
    return `${next.toISOString().slice(0, 19)}Z`;
  }
  return timezone
    ? instantToZonedIso(next, timezone)
    : atFixedOffset(next, zone);
}

// Giving a calendar day a time makes it a datetime: in the named timezone when there
// is one, else on the viewer's wall clock
export function writeTimedValue(
  next: Date,
  like: string,
  timezone?: string
): string {
  if (like !== '' && !isDateOnly(like)) {
    return writeDateValue(next, like, timezone);
  }
  return timezone
    ? instantToZonedIso(next, timezone)
    : format(next, "yyyy-MM-dd'T'HH:mm:ss");
}

export const withDayOf = (current: Date, day: Date): Date =>
  new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    current.getHours(),
    current.getMinutes(),
    current.getSeconds()
  );

// `time` is "HH:mm", as the app's time picker gives it
export function withTimeOf(current: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number);
  const next = new Date(current);
  next.setHours(hours, minutes, 0, 0);
  return next;
}

// The end that keeps the range's length when its start moves. An all-day range
// keeps its number of days, which a DST change inside it would break in hours
export function shiftEnd({
  start,
  end,
  nextStart,
  allDay,
}: {
  start: Date;
  end: Date;
  nextStart: Date;
  allDay: boolean;
}): Date {
  if (allDay) {
    return addDays(end, differenceInCalendarDays(nextStart, start));
  }
  return new Date(end.getTime() + (nextStart.getTime() - start.getTime()));
}

export type DatePreset = 'today' | 'tomorrow' | 'nextWeek';

export function presetDay(preset: DatePreset, now: Date = new Date()): Date {
  const today = startOfDay(now);
  if (preset === 'today') return today;
  if (preset === 'tomorrow') return addDays(today, 1);
  return nextMonday(today);
}

export function dayLabel(date: Date, now: Date = new Date()): string {
  const days = differenceInCalendarDays(date, now);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return format(
    date,
    isSameYear(date, now) ? 'EEE, MMM d' : 'EEE, MMM d, yyyy'
  );
}
